"use client";

import { LuCheck } from "react-icons/lu";
import {
  Section,
  Container,
  Header,
  Badge,
  Title,
  Description,
  Grid,
  PlanCard,
  FeaturedTag,
  PlanContent,
  PlanTitle,
  PlanDescription,
  PlanPrice,
  PlanFeatures,
  PlanFeature,
  PlanButton,
  FooterText,
  FeatureIcon,
  PlanBillingType
} from "./styles";
import SubscriptionService from "@/Services/Subscription";
import { toast } from "react-toastify";
import {
  subscriptionPlans as plans,
  parsePlanPrice
} from "@/domain/Subscription";
import { selectAccessToken, useSelector } from "@/lib/redux";
import { track } from "@/lib/analytics/track";

type PricingProps = {
  page: "lp" | "assinaturas";
};

export default function Pricing({ page }: PricingProps) {
  const accessToken = useSelector(selectAccessToken);
  const userEmail = useSelector((state) => state.user.email);
  const authenticated = Boolean(accessToken);

  const handlePlan = async (plan: (typeof plans)[0]) => {
    if (authenticated) {
      track({
        event: "begin_checkout",
        plan_name: plan.name,
        billing_cycle: plan.billingType,
        value: parsePlanPrice(plan.price),
        currency: "BRL",
        user_email: userEmail ?? ""
      });
    } else {
      track({
        event: "pricing_plan_clicked",
        plan_name: plan.name,
        billing_cycle: plan.billingType,
        authenticated: false
      });
    }

    try {
      const {
        data: { url }
      } = await SubscriptionService.createCheckoutSession({
        priceId: plan.id,
        successUrl:
          process.env.NEXT_PUBLIC_FRONT + "/home?subscriptionSuccess=1",
        cancelUrl: process.env.NEXT_PUBLIC_FRONT + "/configuracao/assinaturas"
      });

      if (url) {
        window.location.href = url;
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Erro ao selecionar o plano. Tente novamente.";
      toast.error(errorMessage);
    }
  };

  return (
    <Section id="precos">
      <Container>
        <Header>
          <Badge>Planos & Preços</Badge>
          <Title>Escolha o plano ideal para sua clínica</Title>
          <Description>
            Oferecemos soluções que se adaptam ao tamanho e às necessidades do
            seu negócio, com pagamento mensal sem fidelidade.
          </Description>
        </Header>

        <Grid>
          {plans.map((plan, index) => (
            <PlanCard key={index} featured={plan.featured}>
              {plan.featured && <FeaturedTag>Mais popular</FeaturedTag>}
              <PlanContent>
                <PlanTitle>{plan.name}</PlanTitle>
                <PlanDescription>{plan.description}</PlanDescription>
                <PlanPrice>
                  R$ {plan.price}
                  <PlanBillingType>
                    {plan.billingType === "monthly"
                      ? "/mês"
                      : "pagamento único"}
                  </PlanBillingType>
                </PlanPrice>

                <PlanFeatures>
                  {plan.features.map((feature, fIndex) => (
                    <PlanFeature key={fIndex} featured={plan.featured}>
                      <FeatureIcon featured={plan.featured}>
                        <LuCheck size={16} />
                      </FeatureIcon>
                      <span
                        style={{
                          fontWeight:
                            (index === 1 || index === 2) && fIndex === 0
                              ? "bold"
                              : "normal"
                        }}
                      >
                        {feature}
                      </span>
                    </PlanFeature>
                  ))}
                </PlanFeatures>

                <PlanButton
                  featured={plan.featured}
                  onClick={() => handlePlan(plan)}
                >
                  {page === "assinaturas"
                    ? `Selecionar ${plan.name}`
                    : plan.cta}
                </PlanButton>
              </PlanContent>
            </PlanCard>
          ))}
        </Grid>

        <FooterText>
          Todos os planos incluem: <strong>14 dias de teste gratuito</strong>,
          suporte técnico, atualizações regulares, e conformidade com a LGPD.
        </FooterText>
      </Container>
    </Section>
  );
}
